// Promise-returning replacements for `window.alert`, `window.confirm` and
// `window.prompt`. They wrap `createModal`, so they:
//   • render with the portal's dark glass-card theme (no plain white box);
//   • render as in-DOM overlay elements rather than the browser's native
//     UA modal — native dialogs collapse fullscreen on some browsers, our
//     overlay stays inside the fullscreen element;
//   • return a Promise so callsites just `await` the answer.
//
// They intentionally mirror the native function signatures as closely as
// possible to keep mechanical conversion of legacy callsites easy.

import { createModal, type ModalSize } from './modal';

interface BaseDialogOptions {
  title?: string;
  body?: string;
  size?: ModalSize;
}

export interface AlertDialogOptions extends BaseDialogOptions {
  /** Text on the only button. Defaults to "OK". */
  okLabel?: string;
}

export interface ConfirmDialogOptions extends BaseDialogOptions {
  /** Confirm-button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel-button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Marks the confirm action as destructive (red). Default false. */
  destructive?: boolean;
}

export interface PromptDialogOptions extends BaseDialogOptions {
  /** Pre-fill value. Defaults to "". */
  defaultValue?: string;
  /** Placeholder when the field is empty. */
  placeholder?: string;
  /** Confirm-button label. Defaults to "OK". */
  confirmLabel?: string;
  /** Cancel-button label. Defaults to "Cancel". */
  cancelLabel?: string;
}

function buildBody(body: HTMLElement, text?: string): void {
  if (!text) return;
  const p = document.createElement('p');
  p.className = 'tkos-modal__body-text';
  p.textContent = text;
  body.appendChild(p);
}

/** Replacement for `window.alert(msg)`. Returns when the user dismisses. */
export function alertDialog(message: string, opts: AlertDialogOptions = {}): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const modal = createModal({
      title: opts.title ?? 'Notice',
      size: opts.size ?? 'sm',
      content: (body) => {
        buildBody(body, message ?? opts.body);
      },
      actions: [
        {
          label: opts.okLabel ?? 'OK',
          variant: 'primary',
          onClick: () => finish(),
        },
      ],
      onClose: () => {
        finish();
        // Defer destroy until the close animation has finished.
        window.setTimeout(() => modal.destroy(), 300);
      },
    });
    modal.open();
  });
}

/** Replacement for `window.confirm(msg)`. Resolves true if confirmed,
 *  false if cancelled (button or backdrop/Escape). */
export function confirmDialog(message: string, opts: ConfirmDialogOptions = {}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let result = false;
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    const modal = createModal({
      title: opts.title ?? 'Confirm',
      size: opts.size ?? 'sm',
      content: (body) => {
        buildBody(body, message ?? opts.body);
      },
      actions: [
        {
          label: opts.cancelLabel ?? 'Cancel',
          variant: 'ghost',
          onClick: () => {
            result = false;
          },
        },
        {
          label: opts.confirmLabel ?? 'Confirm',
          variant: opts.destructive ? 'danger' : 'primary',
          onClick: () => {
            result = true;
          },
        },
      ],
      onClose: () => {
        finish();
        window.setTimeout(() => modal.destroy(), 300);
      },
    });
    modal.open();
  });
}

/** Replacement for `window.prompt(msg, default)`. Resolves the string the
 *  user entered, or null if they cancelled. */
export function promptDialog(message: string, opts: PromptDialogOptions = {}): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    let result: string | null = null;
    let settled = false;
    let input: HTMLInputElement | null = null;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    const modal = createModal({
      title: opts.title ?? 'Enter a value',
      size: opts.size ?? 'sm',
      content: (body) => {
        buildBody(body, message ?? opts.body);
        const wrap = document.createElement('div');
        wrap.className = 'tkos-modal__prompt-field';
        input = document.createElement('input');
        input.type = 'text';
        input.className = 'tkos-modal__prompt-input';
        input.value = opts.defaultValue ?? '';
        if (opts.placeholder) input.placeholder = opts.placeholder;
        // Enter submits with the current value as if the OK button was
        // pressed; Escape falls through to the modal's default cancel.
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            result = input?.value ?? '';
            modal.close();
          }
        });
        wrap.appendChild(input);
        body.appendChild(wrap);
        // Focus after the modal lays out so the input gets focus, not the
        // first button. createModal's auto-focus already prefers non-close
        // focusable elements — the input is the first such element here.
      },
      actions: [
        {
          label: opts.cancelLabel ?? 'Cancel',
          variant: 'ghost',
          onClick: () => {
            result = null;
          },
        },
        {
          label: opts.confirmLabel ?? 'OK',
          variant: 'primary',
          onClick: () => {
            result = input?.value ?? '';
          },
        },
      ],
      onClose: () => {
        finish();
        window.setTimeout(() => modal.destroy(), 300);
      },
    });
    modal.open();
  });
}
